import type { UIMessage } from "ai"

function getTextPartValue(part: unknown) {
  if (
    part &&
    typeof part === "object" &&
    "type" in part &&
    (part as { type?: unknown }).type === "text" &&
    "text" in part &&
    typeof (part as { text?: unknown }).text === "string"
  ) {
    return (part as { text: string }).text
  }

  return ""
}

function getMessagePartType(part: unknown) {
  if (
    part &&
    typeof part === "object" &&
    "type" in part &&
    typeof (part as { type?: unknown }).type === "string"
  ) {
    return (part as { type: string }).type
  }

  return null
}

export function getTextSegmentsFromMessageParts(parts: unknown[]) {
  const segments: string[] = []
  let currentSegment = ""

  function flushCurrentSegment() {
    const normalized = currentSegment.trim()

    if (normalized) {
      segments.push(normalized)
    }

    currentSegment = ""
  }

  for (const part of parts) {
    const text = getTextPartValue(part)

    if (text) {
      currentSegment += text
      continue
    }

    const partType = getMessagePartType(part)

    if (partType === "step-start" || (partType && partType !== "text")) {
      flushCurrentSegment()
    }
  }

  flushCurrentSegment()

  return segments
}

function joinTextSegments(segments: string[]) {
  return segments.reduce((combined, segment) => {
    const left = combined.trimEnd()
    const right = segment.trimStart()

    if (!left) {
      return right
    }

    if (!right) {
      return left
    }

    const shouldStartNewBlock =
      /^(#{1,6}\s|>\s|[-*]\s|\d+\.\s|```)/.test(right)

    if (shouldStartNewBlock) {
      return `${left}\n\n${right}`
    }

    const needsNoSeparator =
      /\s$/.test(combined) ||
      /^[,.;:!?)}\]]/.test(right) ||
      /[(\[{/]$/.test(left)

    return needsNoSeparator ? `${left}${right}` : `${left} ${right}`
  }, "")
}

export function getTextFromMessageParts(parts: unknown[]) {
  return joinTextSegments(getTextSegmentsFromMessageParts(parts))
}

export function getLatestUserText(messages: UIMessage[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]

    if (message.role !== "user" || !Array.isArray(message.parts)) {
      continue
    }

    const text = message.parts
      .map(part => getTextPartValue(part))
      .filter(Boolean)
      .join(" ")
      .trim()

    if (text) {
      return text
    }
  }

  return ""
}

export function getLatestUserMessage(messages: UIMessage[]) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]

    if (message.role !== "user" || !Array.isArray(message.parts)) {
      continue
    }

    const text = getTextFromMessageParts(message.parts)

    if (text) {
      return {
        ...message,
        parts: [{ type: "text" as const, text }],
      } satisfies UIMessage
    }
  }

  return null
}
